import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { IntegrationService } from '../../services/api/integration';

interface IMAPServerPreset {
  id: string;
  provider_name: string;
  display_name: string;
  imap_host: string;
  imap_port: number;
  imap_security: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_security?: string;
  notes?: string;
}

interface IMAPConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editConnection?: any; // Existing connection to edit
}

const IMAPConnectionDialog: React.FC<IMAPConnectionDialogProps> = ({
  open,
  onClose,
  onSuccess,
  editConnection,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [presets, setPresets] = useState<IMAPServerPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<IMAPServerPreset | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  
  // Form fields
  const [formData, setFormData] = useState({
    connectionName: '',
    emailAddress: '',
    imapHost: '',
    imapPort: 993,
    imapSecurity: 'SSL/TLS',
    imapUsername: '',
    imapPassword: '',
    smtpHost: '',
    smtpPort: 587,
    smtpSecurity: 'STARTTLS',
    smtpUsername: '',
    smtpPassword: '',
    syncFolders: ['INBOX', 'Sent'],
  });

  const integrationService = useMemo(() => new IntegrationService(), []);

  const loadPresets = useCallback(async () => {
    try {
      const response = await integrationService.getIMAPPresets();
      setPresets(response.presets);
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  }, [integrationService]);

  useEffect(() => {
    if (open) {
      loadPresets();
      if (editConnection) {
        // Populate form with existing connection data (handle snake_case from backend)
        setFormData({
          connectionName: editConnection.connection_name || '',
          emailAddress: editConnection.email_address || '',
          imapHost: editConnection.imap_host || '',
          imapPort: editConnection.imap_port || 993,
          imapSecurity: editConnection.imap_security || 'SSL/TLS',
          imapUsername: editConnection.imap_username || '',
          imapPassword: '', // Don't populate password for security
          smtpHost: editConnection.smtp_host || '',
          smtpPort: editConnection.smtp_port || 587,
          smtpSecurity: editConnection.smtp_security || 'STARTTLS',
          smtpUsername: editConnection.smtp_username || '',
          smtpPassword: '', // Don't populate password for security
          syncFolders: editConnection.sync_folders || ['INBOX', 'Sent'],
        });
      }
    }
  }, [open, editConnection, loadPresets]);

  const handlePresetSelect = (preset: IMAPServerPreset | null) => {
    if (preset) {
      setSelectedPreset(preset);
      setFormData({
        ...formData,
        imapHost: preset.imap_host,
        imapPort: preset.imap_port,
        imapSecurity: preset.imap_security,
        smtpHost: preset.smtp_host || '',
        smtpPort: preset.smtp_port || 587,
        smtpSecurity: preset.smtp_security || 'STARTTLS',
      });
      // Switch to manual tab to show the populated fields
      setTabValue(1);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);

    try {
      const result = await integrationService.testIMAPConnection({
        emailAddress: formData.emailAddress,
        imapHost: formData.imapHost,
        imapPort: formData.imapPort,
        imapSecurity: formData.imapSecurity,
        imapUsername: formData.imapUsername,
        imapPassword: formData.imapPassword,
        smtpHost: formData.smtpHost,
        smtpPort: formData.smtpPort,
        smtpSecurity: formData.smtpSecurity,
        smtpUsername: formData.smtpUsername || formData.imapUsername,
        smtpPassword: formData.smtpPassword || formData.imapPassword,
      });

      if (result.success) {
        setError(null);
        // Show success message
        alert('Connection test successful!');
      } else {
        setError(result.error || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.connectionName || !formData.emailAddress || !formData.imapHost || 
        !formData.imapUsername || !formData.imapPassword) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editConnection) {
        // Update existing connection
        await integrationService.updateIMAPConnection(editConnection.id, formData);
      } else {
        // Create new connection
        await integrationService.createIMAPConnection({
          ...formData,
          smtpUsername: formData.smtpUsername || formData.imapUsername,
          smtpPassword: formData.smtpPassword || formData.imapPassword,
        });
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save connection');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      connectionName: '',
      emailAddress: '',
      imapHost: '',
      imapPort: 993,
      imapSecurity: 'SSL/TLS',
      imapUsername: '',
      imapPassword: '',
      smtpHost: '',
      smtpPort: 587,
      smtpSecurity: 'STARTTLS',
      smtpUsername: '',
      smtpPassword: '',
      syncFolders: ['INBOX', 'Sent'],
    });
    setSelectedPreset(null);
    setError(null);
    setTabValue(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editConnection ? 'Edit IMAP Connection' : 'Add IMAP Email Connection'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} style={{ marginBottom: 24 }}>
          <Tab label="Quick Setup" />
          <Tab label="Manual Configuration" />
        </Tabs>

        {tabValue === 0 && (
          <div>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select your email provider for automatic configuration
            </Typography>

            <Autocomplete
              options={presets}
              getOptionLabel={(option) => option?.display_name || ''}
              value={selectedPreset}
              onChange={(e, value) => handlePresetSelect(value)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Email Provider" fullWidth />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props as any;
                return (
                  <li key={key} {...otherProps}>
                    <div>
                      <Typography variant="body1">{option.display_name}</Typography>
                      {option.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {option.notes}
                        </Typography>
                      )}
                    </div>
                  </li>
                );
              }}
            />

            {selectedPreset && selectedPreset.notes && (
              <Alert severity="info" style={{ marginTop: 16 }}>
                <Typography variant="body2">{selectedPreset.notes}</Typography>
              </Alert>
            )}
          </div>
        )}

        {tabValue === 1 && (
          <div>
            <TextField
              fullWidth
              label="Connection Name"
              value={formData.connectionName}
              onChange={(e) => handleInputChange('connectionName', e.target.value)}
              margin="normal"
              required
              helperText="A friendly name for this connection"
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.emailAddress}
              onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              margin="normal"
              required
            />

            <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
              IMAP Settings (Incoming Mail)
            </Typography>

            <div style={{ display: 'flex', gap: 16 }}>
              <TextField
                fullWidth
                label="IMAP Server"
                value={formData.imapHost}
                onChange={(e) => handleInputChange('imapHost', e.target.value)}
                margin="normal"
                required
              />
              
              <TextField
                label="Port"
                type="number"
                value={formData.imapPort}
                onChange={(e) => handleInputChange('imapPort', parseInt(e.target.value))}
                margin="normal"
                style={{ width: 120 }}
                required
              />
            </div>

            <FormControl fullWidth margin="normal">
              <InputLabel>Security</InputLabel>
              <Select
                value={formData.imapSecurity || 'SSL/TLS'}
                onChange={(e) => handleInputChange('imapSecurity', e.target.value)}
                label="Security"
              >
                <MenuItem value="SSL/TLS">SSL/TLS</MenuItem>
                <MenuItem value="STARTTLS">STARTTLS</MenuItem>
                <MenuItem value="None">None (Not Recommended)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Username"
              value={formData.imapUsername}
              onChange={(e) => handleInputChange('imapUsername', e.target.value)}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.imapPassword}
              onChange={(e) => handleInputChange('imapPassword', e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 8 }}>
              SMTP Settings (Outgoing Mail) - Optional
            </Typography>

            <div style={{ display: 'flex', gap: 16 }}>
              <TextField
                fullWidth
                label="SMTP Server"
                value={formData.smtpHost}
                onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                margin="normal"
              />
              
              <TextField
                label="Port"
                type="number"
                value={formData.smtpPort}
                onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                margin="normal"
                style={{ width: 120 }}
              />
            </div>

            <FormControl fullWidth margin="normal">
              <InputLabel>Security</InputLabel>
              <Select
                value={formData.smtpSecurity || 'STARTTLS'}
                onChange={(e) => handleInputChange('smtpSecurity', e.target.value)}
                label="Security"
              >
                <MenuItem value="SSL/TLS">SSL/TLS</MenuItem>
                <MenuItem value="STARTTLS">STARTTLS</MenuItem>
                <MenuItem value="None">None (Not Recommended)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="SMTP Username (leave blank to use IMAP username)"
              value={formData.smtpUsername}
              onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
              margin="normal"
            />

            <TextField
              fullWidth
              label="SMTP Password (leave blank to use IMAP password)"
              type={showSmtpPassword ? 'text' : 'password'}
              value={formData.smtpPassword}
              onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      edge="end"
                    >
                      {showSmtpPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || testing}>
          Cancel
        </Button>
        <Button
          onClick={handleTestConnection}
          disabled={loading || testing || !formData.imapHost || !formData.imapUsername || !formData.imapPassword}
          startIcon={testing ? <CircularProgress size={16} /> : null}
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || testing}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : editConnection ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IMAPConnectionDialog;